import { observer } from 'mobx-react'
import { Icons, Icon } from '../icon'
import React, { forwardRef, useEffect } from 'react'
import cx from 'classnames'
import RoundButton from '../RoundButton'
import {
  getDialogHandlers,
  useListenDialogClose,
  useListenDialogOpen,
} from '@/utils/dialog'
import { kebabCase } from 'lodash'
import { useLocation } from 'react-router-dom'

interface DialogProps {
  className?: string
  isArticle?: boolean
  isRow?: boolean
  headerImage?: string
  icon?: string
  title: string
  text?: string
  children?: React.ReactNode
  actions?: React.ReactNode
  // NOTE: Useful for loading states.
  blockClose?: boolean
  hasVisibleBackground?: boolean
  onClose: () => void
  allowCloseOnEscape?: boolean
  dataScreenshotsId?: string
}

const Dialog = forwardRef<HTMLDialogElement, Omit<DialogProps, 'ref'>>(
  (props, ref) => {
    const {
      className,
      isArticle,
      isRow,
      headerImage,
      icon,
      title,
      text,
      children,
      actions,
      blockClose,
      hasVisibleBackground,
      onClose,
      allowCloseOnEscape = true,
      dataScreenshotsId,
    } = props

    const location = useLocation()

    const { handleOpen } = getDialogHandlers(
      ref as React.RefObject<HTMLDialogElement>,
    )

    useEffect(() => {
      // Open dialog if URL hash matches this dialog's title. ONLY ON INITIAL LOAD.
      if (location.hash === `#${kebabCase(title)}`) {
        handleOpen()
      }
    }, [])

    const replaceURLHash = (hash?: string) => {
      const url = new URL(window.location.href)
      if (hash) {
        url.hash = hash
      } else {
        url.hash = ''
      }
      window.history.replaceState(null, '', url.toString())
    }

    useListenDialogOpen(ref as React.RefObject<HTMLDialogElement>, () => {
      replaceURLHash(`#${kebabCase(title)}`)
    })

    useListenDialogClose(ref as React.RefObject<HTMLDialogElement>, () => {
      replaceURLHash('')
    })

    // Handle browser back button.
    // Open dialog when new URL has this dialog's hash.
    // Close dialog when hash gets removed from URL.
    useEffect(() => {
      const handleHashChange = (ev: HashChangeEvent) => {
        const dialogElement = ref as React.RefObject<HTMLDialogElement>

        if (!dialogElement.current) {
          return
        }

        const { oldURL, newURL } = ev
        const dialogHash = `#${kebabCase(title)}`

        const hashRemoved =
          oldURL.includes(dialogHash) && !newURL.includes(dialogHash)

        const hashAdded =
          !oldURL.includes(dialogHash) && newURL.includes(dialogHash)

        if (hashAdded) {
          handleOpen()
        } else if (hashRemoved) {
          onClose()
        }
      }

      window.addEventListener('hashchange', handleHashChange)

      return () => {
        window.removeEventListener('hashchange', handleHashChange)
      }
    }, [])

    const handleCloseButtonClick = () => {
      if (blockClose) {
        return
      }

      onClose()
    }

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (blockClose) {
        return
      }

      if (event.key === 'Escape') {
        if (allowCloseOnEscape) {
          onClose()
        } else {
          event.preventDefault()
        }
        return
      }
    }

    const handleClickOutside = (event: React.MouseEvent) => {
      if (blockClose) {
        return
      }

      const dialogElement = ref as React.RefObject<HTMLDialogElement>
      if (event.target === dialogElement.current) {
        onClose()
      }
    }

    return (
      <dialog
        ref={ref}
        className={cx(
          'dialog',
          {
            dialogArticle: isArticle,
            'has-visible-background': hasVisibleBackground,
          },
          className,
        )}
        onKeyDown={handleKeyDown}
        onClick={handleClickOutside}
      >
        <div className="dialog-wrapper" data-screenshots-id={dataScreenshotsId}>
          {isArticle ? (
            <header className="dialogArticle-header">
              <div className="dialogArticle-headerImage">
                <img src={headerImage} alt="Imagen de cabecera" />
              </div>
              <h2 className="dialogArticle-title">
                {icon ? <Icon size={24} name={icon} /> : null}
                {title}
              </h2>
              <RoundButton
                icon={Icons.Cross}
                className="dialog-close"
                label="Cerrar cuadro de diálogo"
                onClick={handleCloseButtonClick}
              />
            </header>
          ) : (
            <header className="dialog-header">
              <RoundButton
                icon={Icons.Cross}
                className="dialog-close"
                label="Cerrar cuadro de diálogo"
                onClick={handleCloseButtonClick}
              />
              <h2 className="dialog-title">
                {icon ? <Icon size={24} name={icon} /> : null}
                {title}
              </h2>
              {text ? <p className="dialog-text">{text}</p> : null}
            </header>
          )}
          {children ? (
            <div className={cx('dialog-content', { 'is-row': isRow })}>
              {children}
            </div>
          ) : null}
          {actions ? (
            <footer className="dialog-footer">{actions}</footer>
          ) : null}
        </div>
      </dialog>
    )
  },
)

Dialog.displayName = 'Dialog'

export default observer(Dialog)
